import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { F_NUM } from 'assets/FormatNumbers'
import { searchJoken, select } from 'types/SearchTypes'
import { getNow, getNumberFromDate, getNumberFromString } from 'utils/dateUtil'
import { convertNumber } from 'utils/numberUtil'

type SearchJuryoState = {
  searchJoken: searchJoken // 商品受領確認証画面検索情報
  searchJokenDetail: searchJoken // 商品受領確認証明細画面検索情報
  detailView: boolean // 明細表示判定
  clickNohinYmd: number // 押下した納品日
  clickTokuiCd: number // 押下した得意先コード
  clickTenCd: number // 押下した店舗コード
  expand: boolean // 検索部分を開いているか
  expandDetail: boolean // 明細画面の検索部分を開いているか
}

const initJoken = {
  JIGYO_CD: F_NUM, // 事業所コード
  NOHIN_YMD_FROM: F_NUM, // 納品日（FROM）
  NOHIN_YMD_TO: getNumberFromDate(getNow()), // 納品日（TO）
  TOKUI_CD: F_NUM, // 得意先コード
  TEN_CD: F_NUM, // 店舗コード
  SHOHIN_CD: F_NUM, // JPコード
  DENPYO_NO: String(F_NUM), // 伝票番号
  JURYO_KBN: {
    mijuryoCheck: false, // 未受領チェックボックス
    juryoCheck: false, // 受領チェックボックス
    naiyoCheck: false, // 内容確認依頼チェックボックス
  },
  keppinCheck: false, // 欠品チェックボックス
}

const initialState: SearchJuryoState = {
  searchJoken: initJoken, // 商品受領確認証画面検索情報
  searchJokenDetail: initJoken, // 商品受領確認証明細画面検索情報
  detailView: false, // 明細表示判定
  clickNohinYmd: F_NUM, // 押下した納品日
  clickTokuiCd: F_NUM, // 押下した得意先コード
  clickTenCd: F_NUM, // 押下した店舗コード
  expand: true, // 検索部分を開いているか
  expandDetail: true, // 明細画面の検索部分を開いているか
}
export const SearchJuryoSlice = createSlice({
  name: 'SearchJuryo',
  initialState: initialState,
  reducers: {
    // 納品日（FROM）を変更する処理 //===========================//
    changeNohinYmdFrom: (state, action: PayloadAction<string>) => {
      state.searchJoken.NOHIN_YMD_FROM = getNumberFromString(action.payload)
    },
    // 納品日（TO）を変更する処理 //===========================//
    changeNohinYmdTo: (state, action: PayloadAction<string>) => {
      state.searchJoken.NOHIN_YMD_TO = getNumberFromString(action.payload)
    },
    // 伝票番号 //===========================//
    changeDenpyoNo: (state, action: PayloadAction<string>) => {
      if (!action.payload) {
        state.searchJoken.DENPYO_NO = initialState.searchJoken.DENPYO_NO
      } else {
        state.searchJoken.DENPYO_NO = action.payload
      }
    },
    // 得意先処理 //===========================//
    changeTokuisakiCode: (state, action: PayloadAction<number>) => {
      state.searchJoken.TOKUI_CD = convertNumber(action.payload, 6)
    },
    // 店舗処理 //===========================//
    changeTenpoCode: (state, action: PayloadAction<number>) => {
      state.searchJoken.TEN_CD = convertNumber(action.payload, 6)
    },
    // 事業所処理 //===========================//
    changeJigyoshoCode: (state, action: PayloadAction<number>) => {
      state.searchJoken.JIGYO_CD = convertNumber(action.payload, 2)
    },
    // JPコード //===========================//
    changeShohinCode: (state, action: PayloadAction<string>) => {
      state.searchJoken.SHOHIN_CD = convertNumber(action.payload, 7)
    },
    // 受領チェックボックスを変更する処理 //===========================//
    changeJuryoCheck: (state) => {
      state.searchJoken.JURYO_KBN.juryoCheck = !state.searchJoken.JURYO_KBN.juryoCheck
    },
    // 未受領チェックボックスを変更する処理 //===========================//
    changeMijuryoCheck: (state) => {
      state.searchJoken.JURYO_KBN.mijuryoCheck = !state.searchJoken.JURYO_KBN.mijuryoCheck
    },
    // 内容確認依頼チェックボックスを変更 //===========================//
    changeNaiyoCheck: (state) => {
      state.searchJoken.JURYO_KBN.naiyoCheck = !state.searchJoken.JURYO_KBN.naiyoCheck
    },
    // 欠品チェックボックスを変更 //===========================//
    changeKeppinCheck: (state) => {
      state.searchJoken.keppinCheck = !state.searchJoken.keppinCheck
    },
    // 確認対象表示状態にする処理 //===========================//
    changeKakunintaisyoCheckbox: (state, action: PayloadAction<string>) => {
      switch (action.payload) {
        case '1': // ジャペル（一般・スーパーユーザー）
          state.searchJoken.JURYO_KBN.mijuryoCheck = true
          state.searchJoken.JURYO_KBN.juryoCheck = false
          state.searchJoken.JURYO_KBN.naiyoCheck = false
          state.searchJoken.keppinCheck = false
          break
        case '3': // 仕入先
          state.searchJoken.JURYO_KBN.mijuryoCheck = true
          state.searchJoken.JURYO_KBN.juryoCheck = false
          state.searchJoken.JURYO_KBN.naiyoCheck = false
          state.searchJoken.keppinCheck = false
          break
      }
    },
    // 明細表示判定を変更 //===========================//
    changeDetailView: (state, action: PayloadAction<boolean>) => {
      state.detailView = action.payload
      if (action.payload) state.expandDetail = false
    },
    // 押下した納品日を変更 //===========================//
    changeClickNohinYmd: (state, action: PayloadAction<number>) => {
      state.clickNohinYmd = convertNumber(action.payload, 8)
    },
    // 押下した得意先コードを変更 //===========================//
    changeClickTokuisakiCode: (state, action: PayloadAction<number>) => {
      state.clickTokuiCd = convertNumber(action.payload, 6)
    },
    // 押下した店舗コードを変更 //===========================//
    changeClickTenpoCode: (state, action: PayloadAction<number>) => {
      state.clickTenCd = convertNumber(action.payload, 6)
    },
    // 納品日（FROM）を変更する処理 //===========================//
    changeNohinYmdFromDetail: (state, action: PayloadAction<string>) => {
      state.searchJokenDetail.NOHIN_YMD_FROM = getNumberFromString(action.payload)
    },
    // 納品日（TO）を変更する処理 //===========================//
    changeNohinYmdToDetail: (state, action: PayloadAction<string>) => {
      state.searchJokenDetail.NOHIN_YMD_TO = getNumberFromString(action.payload)
    },
    // 得意先処理 //===========================//
    changeTokuisakiCodeDetail: (state, action: PayloadAction<select>) => {
      if (action.payload != null) {
        state.searchJokenDetail.TOKUI_CD = convertNumber(action.payload.value, 6)
      }
    },
    // 店舗処理 //===========================//
    changeTenpoCodeDetail: (state, action: PayloadAction<select>) => {
      if (action.payload != null) {
        state.searchJokenDetail.TEN_CD = convertNumber(action.payload.value, 6)
      }
    },
    // JPコード //===========================//
    changeShohinCodeDetail: (state, action: PayloadAction<string>) => {
      state.searchJokenDetail.SHOHIN_CD = convertNumber(action.payload, 7)
    },
    // 受領チェックボックスを変更する処理 //===========================//
    changeJuryoCheckDetail: (state, action: PayloadAction<boolean>) => {
      state.searchJokenDetail.JURYO_KBN.juryoCheck = action.payload
    },
    // 未受領チェックボックスを変更する処理 //===========================//
    changeMijuryoCheckDetail: (state, action: PayloadAction<boolean>) => {
      state.searchJokenDetail.JURYO_KBN.mijuryoCheck = action.payload
    },
    // 内容確認依頼チェックボックスを変更 //===========================//
    changeNaiyoCheckDetail: (state, action: PayloadAction<boolean>) => {
      state.searchJokenDetail.JURYO_KBN.naiyoCheck = action.payload
    },
    // 欠品チェックボックスを変更 //===========================//
    changeKeppinCheckDetail: (state, action: PayloadAction<boolean>) => {
      state.searchJokenDetail.keppinCheck = action.payload
    },
    // 明細を閉じる処理 //===========================//
    closeDetail: (state) => {
      state.detailView = false
      state.clickNohinYmd = initialState.clickNohinYmd
      state.clickTokuiCd = initialState.clickTokuiCd
      state.clickTenCd = initialState.clickTenCd
      state.searchJokenDetail = initialState.searchJokenDetail
    },
    // 検索部分開閉 //===========================//
    changeExpand: (state, action: PayloadAction<boolean>) => {
      state.expand = action.payload
    },
    // 明細画面の検索部分開閉 //===========================//
    changeExpandDetail: (state, action: PayloadAction<boolean>) => {
      state.expandDetail = action.payload
    },
    // 初期状態に設定する処理 //===========================//
    initialize: (state, action: PayloadAction<{ isJapell: boolean; isSuper?: boolean; JIGYO_CD?: number }>) => {
      // 全初期化
      for (const key in state) {
        const tempKey: keyof SearchJuryoState = key as keyof SearchJuryoState
        ;(state as any)[tempKey] = initialState[tempKey]
      }
      const payload = action.payload
      if (payload.isJapell) {
        // ジャペルユーザーの場合
        // state.searchJoken.JURYO_KBN.mijuryoCheck = true // 未受領
        state.searchJoken = {
          ...state.searchJoken,
          // スーパーユーザーでないなら所属事業所を設定
          JIGYO_CD: !payload.isSuper ? payload.JIGYO_CD! : initialState.searchJoken.JIGYO_CD,
          JURYO_KBN: {
            ...state.searchJoken.JURYO_KBN,
            mijuryoCheck: true, // 未受領
          },
        }
      } else {
        // 得意先ユーザーの場合
        state.searchJoken = {
          ...state.searchJoken,
          JURYO_KBN: {
            ...state.searchJoken.JURYO_KBN,
            mijuryoCheck: true, // 未受領
          },
        }
      }
    },
    // 検索条件以外の条件初期化 //===========================//
    formatExceptJotai: (state) => {
      state.searchJoken = {
        ...initialState.searchJoken,
        keppinCheck: state.searchJoken.keppinCheck,
      }
    },
    // 条件初期化 //===========================//
    format: (state) => {
      state.searchJoken = {
        ...initialState.searchJoken,
        keppinCheck: initJoken.keppinCheck,
      }
    },
    // 明細条件初期化 //===========================//
    formatDetail: (state) => {
      state.searchJokenDetail = {
        ...initialState.searchJokenDetail,
        keppinCheck: initJoken.keppinCheck,
      }
    },
    // 受領情報 初期化 //===========================//
    formatAll: (state) => {
      for (const key in state) {
        const tempKey: keyof SearchJuryoState = key as keyof SearchJuryoState
        ;(state as any)[tempKey] = initialState[tempKey]
      }
    },
  },
})

// actionCreator 書き忘れないこと！
export const {
  changeKakunintaisyoCheckbox, // 確認対象表示状態にする処理
  changeNohinYmdFrom, // 納品日（FROM）を変更する処理
  changeNohinYmdTo, // 納品日（TO）を変更する処理
  changeDenpyoNo, // 条件管理番号
  changeTokuisakiCode, // 得意先処理
  changeTenpoCode, // 店舗処理
  changeShohinCode, // JPコード処理
  changeJigyoshoCode, // 事業所処理
  initialize, // 初期状態に設定する処理
  changeMijuryoCheck, // 未受領チェックボックスを変更する処理
  changeJuryoCheck, // 受領チェックボックスを変更する処理
  changeNaiyoCheck, // 内容確認依頼チェックボックスを変更
  changeKeppinCheck, // 欠品チェックボックスを変更
  changeDetailView, // 明細表示判定を変更
  changeClickNohinYmd, // 押下した納品日を変更
  changeClickTokuisakiCode, // 押下した得意先コードを変更
  changeClickTenpoCode, // 押下した店舗コードを変更
  changeNohinYmdFromDetail, // 納品日（FROM）を変更する処理
  changeNohinYmdToDetail, // 納品日（TO）を変更する処理
  changeTokuisakiCodeDetail, // 得意先処理
  changeTenpoCodeDetail, // 店舗処理
  changeShohinCodeDetail, // JPコード処理
  changeMijuryoCheckDetail, // 未受領チェックボックスを変更する処理
  changeJuryoCheckDetail, // 受領チェックボックスを変更する処理
  changeNaiyoCheckDetail, // 内容確認依頼チェックボックスを変更
  changeKeppinCheckDetail, // 欠品チェックボックスを変更
  closeDetail, // 明細を閉じる
  changeExpand, // 検索部分開閉
  changeExpandDetail, // 明細画面の検索部分開閉
  formatExceptJotai, // 検索条件以外の条件初期化
  format, // 条件初期化
  formatDetail, // 明細条件初期化
  formatAll, // 受領情報 初期化
} = SearchJuryoSlice.actions
export default SearchJuryoSlice.reducer
